import React, { useCallback } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TDeviceSelectProps } from '@/console/features/devices/components/device-select/device-select.types';
import { useDeviceSelectOptions } from '@/console/features/devices/hooks/use-device-select-options/use-device-select-options';
import { TSelectOption } from '@/shared/components/ui/form/components/searchable-select/base-searchable-select.types';
import { AsyncSearchableSelect } from '@/shared/components/ui/form/components/searchable-select/variant/async/async-searchable-select';
import { useReadAllDevicesQuery } from '@/shared/redux/api/assets/devices/devices-api';
import {
  DeviceFragment,
  ReadAllDevicesQuery,
} from '@/shared/types/types.generated';

const EMPTY_ADDITIONAL_OPTIONS: TSelectOption<never>[] = [];

export const DeviceSelect = <
  TFieldValues extends FieldValues,
  TOptionShape = string,
>({
  name,
  deviceFilter,
  getItemId,
  createOption,
  disableTags = true,
  enableTagsTooltip,
  filterSelectedTags,
  additionalOptions = EMPTY_ADDITIONAL_OPTIONS as TSelectOption<TOptionShape>[],
  onSelectChange,
  required,
  rtkQueryArgs,
  optionProps,
  disabled,
  multiple = true,
}: TDeviceSelectProps<TFieldValues, TOptionShape>) => {
  const { t } = useTranslation();
  const { getAdditionalInfo, isDisabled } = useDeviceSelectOptions(optionProps);

  const createDefaultOption = useCallback(
    (device: DeviceFragment) => {
      return {
        label: device.name,
        value: device.deviceId,
        additionalInfo: getAdditionalInfo(device),
        disabled: isDisabled(device),
      } as TSelectOption<TOptionShape>;
    },
    [getAdditionalInfo, isDisabled],
  );
  const _createOption = createOption ? createOption : createDefaultOption;

  // Stable identity so the memoized `transformData(data)` result in
  // use-async-options.ts isn't invalidated purely by DeviceSelect re-rendering
  // (e.g. from a polling parent) when the underlying query data hasn't changed.
  const transformData = useCallback(
    (data?: ReadAllDevicesQuery) => {
      if (!data) {
        return [];
      }

      const devices = data.readAllDevices.items;
      const validDevices = deviceFilter
        ? devices.filter((device) => deviceFilter(device))
        : devices;

      const options = validDevices.map((device) => _createOption(device));

      return [...additionalOptions, ...options];
    },
    [deviceFilter, _createOption, additionalOptions],
  );

  return (
    <AsyncSearchableSelect<TFieldValues, ReadAllDevicesQuery, TOptionShape>
      name={name}
      label={t('device:select_devices', { count: multiple ? 2 : 1 })}
      placeholder={t('device:search_devices', { count: multiple ? 2 : 1 })}
      disableTags={disableTags}
      enableTagsTooltip={enableTagsTooltip}
      filterSelectedTags={filterSelectedTags}
      multiple={multiple}
      getItemId={getItemId}
      optionProps={{
        useRTKQuery: useReadAllDevicesQuery,
        transformData,
        rtkQueryArgs,
      }}
      onSelectChange={onSelectChange}
      required={required}
      disabled={disabled}
    />
  );
};
